/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

// will be commented by GBC build tool if built the old way
import jsface from "jsface";

(function (context) {
  // in case a of Vite module, context.jsface doesn't exist, use imported instead
  const nativeJsface = context.jsface || jsface;
  const noop = function () {};

  /**
   * Detects a native ES6 class. Native class constructors have a non-writable
   * `prototype`, whereas plain functions (including jsface-created classes) have a
   * writable one.
   * @param {*} fn candidate
   * @returns {boolean} true if fn is a native ES6 class
   */
  const isNativeClass = function (fn) {
    if (typeof fn !== "function" || !fn.prototype) {
      return false;
    }
    const descriptor = Object.getOwnPropertyDescriptor(fn, "prototype");
    return Boolean(descriptor) && descriptor.writable === false;
  };

  /**
   * Collects the (possibly non-enumerable) methods of a native class prototype chain
   * into the `$super` accessor, so `$super.someMethod.call(this)` keeps working when a
   * jsface class extends a native ES6 class. Most-derived definitions win.
   * @param {Object} $super the $super accessor to populate
   * @param {Object} parentProto the native parent's prototype
   */
  const collectNativeSuper = function ($super, parentProto) {
    let proto = parentProto;
    while (proto && proto !== Object.prototype) {
      for (const key of Object.getOwnPropertyNames(proto)) {
        if (key === "constructor" || Object.prototype.hasOwnProperty.call($super, key)) {
          continue;
        }
        const descriptor = Object.getOwnPropertyDescriptor(proto, key);
        if (descriptor && typeof descriptor.value === "function") {
          $super[key] = descriptor.value;
        }
      }
      proto = Object.getPrototypeOf(proto);
    }
  };

  const nativeExtendWarned = new Set();
  /**
   * Warns (once per native parent) that extending a native ES6 class through
   * `oo.Class()` uses a deprecated compatibility bridge.
   * @param {Function} parent the native parent class
   * @param {Object} api the child api
   */
  const warnNativeExtendDeprecation = function (parent, api) {
    if (nativeExtendWarned.has(parent)) {
      return;
    }
    nativeExtendWarned.add(parent);
    if (typeof gbc !== "undefined" && typeof gbc.warn === "function") {
      const parentName = (parent.prototype && parent.prototype.__name) || parent.name || "a native ES6 class";
      const childName = (api && api.__name) || "a class";
      gbc.warn(
        "[jsface][deprecated] " +
          childName +
          " extends native ES6 class " +
          parentName +
          " through oo.Class(). This compatibility bridge is deprecated: migrate " +
          childName +
          " to a native ES6 class."
      );
    }
  };

  /**
   * @typedef {Function} ClassFunctor
   * @param {Object.<string, function|*>} $super
   */

  /**
   * ### __jsface override__
   * adds some new features:
   *
   * * introduce $super keyword
   * * ability to declare mixins in class definition
   *
   * See {@link augmentedFace.Class} to create classes
   * @namespace window.augmentedFace
   */
  context.augmentedFace = /** @lends window.augmentedFace */ {
    /**
     * Creates a class
     * @example
     * // Simple class, no inheritance
     * var MyClass = augmentedFace.Class({
     *   myMethod : function(){
     *     this.myMember = 1;
     *   }
     * });
     * @example
     * // Simple class, inherits from MyClass, overrides myMethod
     * augmentedFace.Class(MyClass, {
     *   myMethod : function(){
     *     this.myMember = 2;
     *   }
     * });
     * @example
     * // Simple class, inherits from MyClass, calls superclass' myMethod
     * augmentedFace.Class(MyClass, function($super){
     *   return {
     *     myMethod : function(){
     *       this.myMember = 2;
     *     }
     *   };
     * });
     * @method Class
     * @param {ClassFunctor|Object|number} [params]
     * if *params* is omitted, the defined class will not have a superclass
     * @param {ClassFunctor|Object|number} api
     * if *api* is omitted, the *params* parameter will be used as api description
     * The *api* parameter can be:
     * * an object, describing the different members of the class
     * * a function, that will return an object as above desciption, but with the ability to inject *$super* accessor
     * @param {?boolean=} _checkMembers internal flag
     * @returns {Function} the constructed class
     */
    Class: function (params, api, _checkMembers) {
      let parent = 0,
        inheritance = [],
        ascendance = [];
      if (!api) {
        api = params;
        params = 0;
      }
      switch (typeof params) {
        case "function":
          parent = params;
          break;
        case "object":
          parent = params.base || 0;
          break;
        default:
          break;
      }

      const $super = {
        constructor: parent || noop
      };

      const nativeParent = isNativeClass(parent) ? parent : null;

      if (parent && nativeParent) {
        // __deprecated__ bridge: allow a jsface class to extend a native ES6 class.
        // Native prototype methods are non-enumerable, so walk the chain to expose them
        // on $super. The native parent is constructed by the child constructor below
        // (via Reflect.construct), so $super.constructor must be a no-op here.
        collectNativeSuper($super, parent.prototype);
        $super.__name = parent.prototype.__name;
        if (parent.prototype.__ascendance) {
          ascendance = parent.prototype.__ascendance.slice();
        }
        if (parent.prototype.__inheritance) {
          inheritance = parent.prototype.__inheritance.slice();
          inheritance.push(parent.prototype.__name);
        }
        $super.constructor = noop;
      } else if (parent) {
        const memberKeys = Object.keys(parent.prototype);
        for (const methodName of memberKeys) {
          const method = parent.prototype[methodName];
          if (typeof method === "function") {
            $super[methodName] = method;
          } else if (methodName === "__name") {
            $super.__name = method;
          } else if (methodName === "__ascendance") {
            ascendance = method.slice();
          } else if (methodName === "__inheritance") {
            inheritance = method.slice();
            inheritance.push($super.__name);
          }
        }
      }

      api = (typeof api === "function" ? api($super) : api) || {};
      if (!api.__name && ascendance.indexOf("WidgetBase") >= 0) {
        throw api;
      }
      if (!api.__virtual) {
        ascendance.push(api.__name);
      }
      if (ascendance.indexOf("WidgetBase") >= 0) {
        this._widgetSpecificApi(api, ascendance);
      }
      api.__inheritance = inheritance;
      if (nativeParent) {
        // Native prototype chain already models the ancestry: use native instanceof.
        api.isInstanceOf = function (type) {
          return this instanceof type;
        };
      } else {
        api.isInstanceOf = function (type) {
          return (
            (Boolean(type) && Boolean(type.prototype) && type.prototype.__name === this.__name) ||
            this.__inheritance.indexOf(type.prototype.__name) >= 0
          );
        };
      }
      if (nativeParent) {
        // __deprecated__ bridge: construct the native parent chain with `new` semantics
        // (Reflect.construct), preserving virtual dispatch to this child's overrides, then
        // run the child's own constructor body on the resulting instance ($super.constructor
        // is a no-op). Note: unlike jsface's in-place init, any work the child constructor
        // performs BEFORE its $super.constructor call runs AFTER the parent is constructed.
        warnNativeExtendDeprecation(parent, api);
        const userConstructor = api.hasOwnProperty("constructor") ? api.constructor : null;
        api.constructor = function () {
          const args = Array.prototype.slice.call(arguments);
          const self = Reflect.construct(nativeParent, args, new.target || api.constructor);
          if (userConstructor) {
            userConstructor.apply(self, args);
          }
          return self;
        };
      } else if (!api.hasOwnProperty("constructor")) {
        api.constructor = function (arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9) {
          $super.constructor.call(this, arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8, arg9);
        };
      }
      if (_checkMembers !== false) {
        this._checkMembers(api);
      }

      return nativeJsface.Class(parent, api);
    },
    _widgetSpecificApi: function (api, ascendance) {
      if (api.__ignoreInheritance) {
        api.__ascendance = ["WidgetBase"];
        if (!api.__virtual) {
          api.__ascendance.push(api.__name);
        }
      } else {
        api.__ascendance = ascendance;
      }
      api.__ascendanceClasses = ascendance
        .map(function (item) {
          return "gbc_" + item;
        })
        .join(" ");
    },
    _checkMembers: function (api) {
      Object.keys(api).forEach(function (mk) {
        switch (mk) {
          case "__inheritance":
          case "__ascendance":
          case "usedStyleAttributes":
          case "__dataContentPlaceholderSelector":
          case "watchedAttributes":
          case "$static":
            return;
          default:
            break;
        }
        switch (typeof api[mk]) {
          case "function":
          case "string":
          case "number":
          case "boolean":
            return;
          case "object":
            if (api[mk] === null) {
              return;
            }
            break;
          default:
            break;
        }
        gbc.warn("class definition issue, member seems shared", api.__name, mk, typeof api[mk]);
      });
    },

    /**
     * Creates a single instance of a class
     * @method Singleton
     * @param {ClassFunctor|Object|number} [params]
     * if *params* is omitted, the defined class will not have a superclass
     * @param {ClassFunctor|Object|number} api
     * if *api* is omitted, the *params* parameter will be used as api description
     * The *api* parameter can be:
     *
     * * an object, describing the different members of the class
     * * a function, that will return an object as above desciption, but with the ability to inject *$super* accessor
     * @returns {*} the constructed instance
     */
    Singleton: function (params, api) {
      const SingletonClass = this.Class(params, api, false);
      SingletonClass.prototype.__unique = true;
      return new SingletonClass();
    },

    /**
     * Creates a static class. Should not be instantiated
     * @method StaticClass
     * @param {ClassFunctor|Object|number} [params]
     * if *params* is omitted, the defined class will not have a superclass
     * @param {ClassFunctor|Object|number} api
     * if *api* is omitted, the *params* parameter will be used as api description
     * The *api* parameter can be:
     * * an object, describing the different members of the class
     * * a function, that will return an object as above desciption, but with the ability to inject *$super* accessor
     * @returns {*} the constructed class
     */
    StaticClass: function (params, api) {
      if (!api) {
        api = params;
        params = 0;
      }
      if (api) {
        if (typeof api === "function") {
          const originalApi = api;
          api = function ($super) {
            const result = originalApi($super);
            if (result) {
              result.$singleton = true;
            }
            return result;
          };
        } else {
          api.$singleton = true;
        }
      }
      return this.Class(params, api, false);
    }
  };
})(window);
